import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { SearchParam } from '@backend/types'
import {
  SceneGroupResponse,
  SceneGroupsResponse,
  SceneGroupCreate,
  SceneGroupUpdate,
  FullSceneGroupResponse,
} from '@backend/sceneGroups/sceneGroupsRouter'
import { SortOrdersBody } from '@backend/types'
import { IId, ISchemaAndId, ISchemaId } from '../../types/common'
import { prepareHeaders, handleApiError } from './helpers'

interface ISearchParams extends SearchParam {}

export interface ISceneGroup extends SceneGroupResponse {}

export interface IFullSceneGroup extends FullSceneGroupResponse {}

export interface ISceneGroupCreate extends SceneGroupCreate {}

export interface ISceneGroupUpdate extends SceneGroupUpdate, IId {}

export const sceneGroupsApi = createApi({
  reducerPath: 'sceneGroups',
  baseQuery: fetchBaseQuery({
    baseUrl: window.env.API_ENDPOINT,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    getSceneGroups: builder.query<
      SceneGroupsResponse,
      ISearchParams & ISchemaId
    >({
      query: ({ schemaId, limit, offset, sort, sortKey, key, value }) => ({
        url: `scene-groups/schema/${schemaId}`,
        params: { limit, offset, sort, sortKey, key, value },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.getSceneGroups', err)
        }
      },
    }),
    addSceneGroup: builder.query<void, ISceneGroupCreate & ISchemaId>({
      query: ({ schemaId, name }) => ({
        url: `scene-groups/schema/${schemaId}`,
        method: 'POST',
        body: {
          name: name.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.addSceneGroup', err)
        }
      },
    }),
    getSceneGroup: builder.query<IFullSceneGroup, ISchemaAndId>({
      query: ({ schemaId, id }) => ({
        url: `scene-groups/schema/${schemaId}/${id}`,
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.getSceneGroup', err)
        }
      },
    }),
    updateSceneGroup: builder.query<void, ISceneGroupUpdate & ISchemaAndId>({
      query: ({ schemaId, id, name }) => ({
        url: `scene-groups/schema/${schemaId}/${id}`,
        method: 'PATCH',
        body: {
          name: name.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.updateSceneGroup', err)
        }
      },
    }),
    deleteSceneGroup: builder.query<void, ISchemaAndId>({
      query: ({ schemaId, id }) => ({
        url: `scene-groups/schema/${schemaId}/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.deleteSceneGroup', err)
        }
      },
    }),
    addScenesToScenGroup: builder.query<
      void,
      { schemaId: number; id: number; sceneIds: number[] }
    >({
      query: ({ schemaId, id, sceneIds }) => ({
        url: `scene-groups/schema/${schemaId}/${id}/scenes`,
        method: 'POST',
        body: {
          ids: sceneIds,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.addScenesToScenGroup', err)
        }
      },
    }),
    removeSceneFromScenGroup: builder.query<
      void,
      { id: number; userId: number }
    >({
      query: ({ id, userId }) => ({
        url: `scene-groups/scene-group/${id}/scenes/${userId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.sceneGroups.errors.removeSceneFromScenGroup', err)
        }
      },
    }),
    updateSortOrder: builder.query<void, SortOrdersBody & ISchemaId>({
      query: ({ schemaId, sortIds }) => ({
        url: `scene-groups/schema/${schemaId}/sort-order`,
        method: 'PATCH',
        body: {
          sortIds,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.updateSortOrder', err)
        }
      },
    }),
  }),
})

export const {
  useLazyGetSceneGroupsQuery,
  useLazyAddSceneGroupQuery,
  useLazyGetSceneGroupQuery,
  useLazyUpdateSceneGroupQuery,
  useLazyDeleteSceneGroupQuery,
  useLazyAddScenesToScenGroupQuery,
  useLazyRemoveSceneFromScenGroupQuery,
  useLazyUpdateSortOrderQuery,
} = sceneGroupsApi
